import cx from "classnames";
import Link from "next/link";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MdCheckCircle, MdError } from "../AppIcons";
import styles from "./Toast.module.scss";

const EXIT_ANIMATION_MS = 200;
const DEFAULT_DURATION_MS = 2500;
const ERROR_DURATION_MS = 5000;

export type ToastVariant = "default" | "success" | "error";

type ToastOptions = {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  actionLabel?: string;
  actionHref?: string;
};

type ActiveToast = ToastOptions & {
  variant: ToastVariant;
  duration: number;
};

type ToastContextValue = {
  showToast: (options: ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [activeToast, setActiveToast] = useState<ActiveToast | null>(null);

  const showToast = useCallback((options: ToastOptions) => {
    const variant = options.variant ?? "default";
    setActiveToast({
      message: options.message,
      variant,
      duration:
        options.duration ??
        (variant === "error" ? ERROR_DURATION_MS : DEFAULT_DURATION_MS),
      actionLabel: options.actionLabel,
      actionHref: options.actionHref,
    });
  }, []);

  const dismissToast = useCallback(() => {
    setActiveToast(null);
  }, []);

  const value = useMemo(
    () => ({
      showToast,
    }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {activeToast && (
        <Toast
          message={activeToast.message}
          variant={activeToast.variant}
          duration={activeToast.duration}
          actionLabel={activeToast.actionLabel}
          actionHref={activeToast.actionHref}
          onDismiss={dismissToast}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
}

type ToastProps = {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  actionLabel?: string;
  actionHref?: string;
  onDismiss?: () => void;
};

function Toast({
  message,
  variant = "default",
  duration = 2500,
  actionLabel,
  actionHref,
  onDismiss,
}: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!onDismiss) return;

    const exitTimeoutId = window.setTimeout(() => {
      setIsExiting(true);
    }, duration);
    const dismissTimeoutId = window.setTimeout(() => {
      onDismiss();
    }, duration + EXIT_ANIMATION_MS);

    return () => {
      window.clearTimeout(exitTimeoutId);
      window.clearTimeout(dismissTimeoutId);
    };
  }, [duration, onDismiss]);

  return (
    <div
      className={cx(styles.toast, {
        [styles.success]: variant === "success",
        [styles.error]: variant === "error",
        [styles.exiting]: isExiting,
      })}
      role={variant === "error" ? "alert" : "status"}
      aria-live={variant === "error" ? "assertive" : "polite"}
    >
      <div className={styles.message}>
        {variant === "success" && (
          <MdCheckCircle className={styles.icon} size={24} />
        )}
        {variant === "error" && (
          <MdError className={styles.icon} size={24} />
        )}
        <span>{message}</span>
      </div>
      {actionLabel && actionHref && (
        <Link href={actionHref} className={styles.action} onClick={onDismiss}>
          {actionLabel}
        </Link>
      )}
    </div>
  );
}
